import React, {
	useMemo, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import * as SessionActions from '../../../stores/actions/session';
import { GreenroomState as GS } from '../../../stores/types/types';
import Help from '../../help/help';
import { closeMetaMenu, setMetaMenuRoute, setMetaMenuState } from '../../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../../stores/types/tabs';
import { HELP_SUBMENU } from '../../../stores/reducers/session';

const stylePackHighlightingRoutes = [
	META_MENU_ROUTES.STYLE_PACKS,
	META_MENU_ROUTES.STYLE_PACK_INFO,
];

interface Props {
	host: boolean;
	helpMenuOpen: boolean;
	showbuilder: boolean;
}

export default function HeaderDrawerButtons(props: Props) {
	const dispatch = useDispatch();
	const {host, helpMenuOpen, showbuilder} = props;
	const {metaMenuState, metaMenuActiveRoute} = useSelector((s: GS) => s.TabsReducer);
	const selectedPack = useSelector((s: GS) => s.UserInterfaceReducer.displayedStylePackInfo);
	const stylepacksOn = useSelector((s: GS) => s.SettingsReducer.featureFlags?.use_title_library);

	const toggleDrawer = (nRoute: string) => {
		if (metaMenuState === META_MENU_STATES.CLOSED) {
			dispatch(setMetaMenuRoute(nRoute));
			dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
		} else if (metaMenuState === META_MENU_STATES.OPEN) {
			if (nRoute !== metaMenuActiveRoute) {
				dispatch(setMetaMenuRoute(nRoute));
			} else {
				dispatch(closeMetaMenu());
			}
		}
	};

	const [
		highlightSettings,
		highlightSharing,
		highlightStylePacks
	] = useMemo(() => {
		if (
			metaMenuState !== META_MENU_STATES.OPENING
			&& metaMenuState !== META_MENU_STATES.OPEN
		) return [false, false, false];

		return [
			metaMenuActiveRoute === META_MENU_ROUTES.EVENT_SETTINGS,
			metaMenuActiveRoute === META_MENU_ROUTES.SHARING,
			stylePackHighlightingRoutes.includes(metaMenuActiveRoute),
		];
	}, [metaMenuState, metaMenuActiveRoute]);

	const toggleHelpMenu = useCallback(() => {
		dispatch(SessionActions.setHelpMenuOpen(!helpMenuOpen));
		dispatch(SessionActions.setActiveHelpSubmenu(HELP_SUBMENU.NONE));
	}, [helpMenuOpen]);

	const [didAnimate, setDidAnimate] = React.useState(false);
	const onStylePacks = () => {
		setDidAnimate(true);
		if (selectedPack) {
			toggleDrawer(META_MENU_ROUTES.STYLE_PACK_INFO);
		} else {
			toggleDrawer(META_MENU_ROUTES.STYLE_PACKS);
		}
	}

	return (<>
		{stylepacksOn && showbuilder && host && (
			<div
				className={classNames(
					'header-small-button button-imprint-shading stylepacks-no-fade',
					{ 
						'drawer-open': highlightStylePacks,
						'stylepacks-open': highlightStylePacks,
						'stylepacks-did-animate': didAnimate && !highlightStylePacks,
					},
				)}
				onClick={onStylePacks}
			>
				<Icon className='stylepacks-no-fade' name={ICONS.TAKES} size={20} color={COLORS.WHITE} />
			</div>
		)}

		<div className="help-center-container stylepacks-do-fade">
			<div
				className={classNames(
					'header-small-button button-imprint-shading',
					{ 'drawer-open': helpMenuOpen }
				)}
				onMouseDown={toggleHelpMenu}
			>
				<Icon name={ICONS.QUESTION} size={20} color={COLORS.WHITE} />
			</div>
			<Help isOpen={helpMenuOpen} onClose={toggleHelpMenu} />
		</div>

		{host && <>
			<div
				className={classNames(
					'header-small-button button-imprint-shading stylepacks-do-fade',
					{ 'drawer-open': highlightSettings },
				)}
				onClick={() => toggleDrawer(META_MENU_ROUTES.EVENT_SETTINGS)}
			>
				<Icon name={ICONS.SETTINGS} size={20} color={COLORS.WHITE} />
			</div>
			<div
				className={classNames(
					'header-small-button button-imprint-shading stylepacks-do-fade',
					highlightSharing && 'drawer-open',
				)}
				onClick={() => toggleDrawer(META_MENU_ROUTES.SHARING)}
			>
				<Icon name={ICONS.SHARE} size={20} color={COLORS.WHITE} />
			</div>
		</>}
	</>);
}
